import React, { FC, useState, useRef, SyntheticEvent } from 'react';
import { Dialog, DialogActions, styled } from '@mui/material';
import ReactCrop, { PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { storage } from '../../../firebase/config';
import { ref } from 'firebase/storage';
import { uploadFile } from '../../../common/utils/file-helpers-utils';
import OvButton from '../atoms/OvButton';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import { useTranslation } from 'react-i18next';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';

interface OvImageCropDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  originalImagePath: string; // local or remote URL
  imageBasePath: string; // folder in Firebase
  onCropSuccess: (croppedImagePath: string) => void;
}

const OvImageCropDialog: FC<OvImageCropDialogProps> = ({
  isOpen,
  onCancel,
  originalImagePath,
  imageBasePath,
  onCropSuccess,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [crop, setCrop] = useState<PixelCrop>({
    unit: 'px',
    x: 25,
    y: 25,
    width: 40,
    height: 80,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);

  const handleImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget;
  };

  const getCroppedCanvas = (): HTMLCanvasElement | null => {
    if (!completedCrop) {
      setCompletedCrop(crop);
    }

    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');

    // How much the image is scaled down from natural to displayed size
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return canvas;
  };

  const rotateCanvasLeft = (
    croppedCanvas: HTMLCanvasElement
  ): HTMLCanvasElement => {
    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = croppedCanvas.height;
    rotatedCanvas.height = croppedCanvas.width;

    const rctx = rotatedCanvas.getContext('2d');
    if (!rctx) return rotatedCanvas;

    rctx.translate(0, croppedCanvas.width);
    rctx.rotate(-Math.PI / 2);

    rctx.drawImage(croppedCanvas, 0, 0);

    return rotatedCanvas;
  };

  const handleCropSave = () => {
    const croppedCanvas = getCroppedCanvas();
    if (!croppedCanvas) return;

    // Rotate left
    const rotatedCanvas = rotateCanvasLeft(croppedCanvas);

    rotatedCanvas.toBlob(async (blob) => {
      if (!blob) return;

      setIsLoading(true);

      try {
        const storageRef = ref(
          storage,
          `${imageBasePath}/${Date.now()}-cropped-rotated.png`
        );

        // Upload the final rotated blob
        const croppedFileUrl = await uploadFile(blob, storageRef);
        onCropSuccess(croppedFileUrl);
      } catch (err) {
        setIsLoading(false);
        console.error('Error uploading cropped+rotated image:', err);
      } finally {
        setIsLoading(false);
      }
    }, 'image/png');
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth maxWidth="sm">
      <div style={{ padding: '1rem' }}>
        <ReactCrop
          crop={crop}
          onChange={(newCrop) => setCrop(newCrop)}
          onComplete={(c) => setCompletedCrop(c)}
        >
          <img
            alt="Crop me"
            src={originalImagePath}
            onLoad={handleImageLoad}
            ref={imgRef}
          />
        </ReactCrop>

        <StyledDialogActions>
          <StyledLightOvButton onClick={onCancel}>
            {t('common.actions.cancel')}
          </StyledLightOvButton>
          <StyledOvButton disabled={isLoading} onClick={handleCropSave}>
            {t('scans.uploadImageCta')}
          </StyledOvButton>
        </StyledDialogActions>

        {isLoading && <OvLoadingIndicator position="fixed" />}
      </div>
    </Dialog>
  );
};

export default OvImageCropDialog;

const StyledDialogActions = styled(DialogActions)`
  && {
    margin-top: 2rem;
    padding: 0 1.5rem 1.5rem 1.5rem;
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem !important;
    align-self: flex-end;
  }
`;

const StyledLightOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0 1rem;
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;
